import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, AlertTriangle, HelpCircle, Download, RefreshCw } from 'lucide-react';

const DeveloperSpeakerId = () => {
  const { organizationId } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === 'dark';
  const [sessions, setSessions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!organizationId) return;
    loadSessions();
  }, [organizationId]);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('sessions')
        .select('id, tutor_name, student_name, identified_tutor_speaker, identified_student_speaker, speaker_id_confidence, speaker_id_raw_response, lesson_timestamp, speakers_identified_at')
        .eq('organization_id', organizationId)
        .not('speakers_identified_at', 'is', null)
        .order('lesson_timestamp', { ascending: false })
        .limit(100);
      setSessions(data || []);
      if (data && data.length > 0) setSelected(data[0]);
    } catch (err) {
      console.error('Error loading speaker ID:', err);
    }
    setLoading(false);
  };

  const isMismatch = (s) => {
    if (!s.identified_tutor_speaker || !s.tutor_name) return false;
    return !s.identified_tutor_speaker.toLowerCase().includes(s.tutor_name.toLowerCase()) &&
           !s.tutor_name.toLowerCase().includes(s.identified_tutor_speaker.toLowerCase());
  };

  const isLowConf = (s) => (s.speaker_id_confidence || '').toLowerCase() === 'low';

  const filtered = sessions.filter(s => {
    if (filter === 'mismatch') return isMismatch(s);
    if (filter === 'low') return isLowConf(s);
    return true;
  });

  const confIcon = (conf) => {
    const c = (conf || '').toLowerCase();
    if (c === 'high') return <CheckCircle className="h-3.5 w-3.5 text-emerald-400" />;
    if (c === 'medium') return <HelpCircle className="h-3.5 w-3.5 text-amber-400" />;
    return <AlertTriangle className="h-3.5 w-3.5 text-red-400" />;
  };

  const cardClass = `rounded-lg border ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate('/developer')} className={`flex items-center gap-2 text-sm ${isDark ? 'text-[#a1a1aa] hover:text-white' : 'text-[#666] hover:text-[#1a1a1a]'}`}>
        <ArrowLeft className="h-4 w-4" /> Back to Developer
      </button>

      <div className="flex items-center justify-between">
        <h1 className={`text-2xl font-semibold font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
          Speaker ID Diagnostics
        </h1>
        <button onClick={loadSessions} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-[#18181b] text-[#a1a1aa]' : 'hover:bg-[#f0f0f0] text-[#666]'}`}>
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-1">
        {[
          { key: 'all', label: `All (${sessions.length})` },
          { key: 'mismatch', label: `Mismatches (${sessions.filter(isMismatch).length})` },
          { key: 'low', label: `Low Confidence (${sessions.filter(isLowConf).length})` },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
              filter === f.key
                ? isDark ? 'bg-[#18181b] text-white' : 'bg-[#e8e8e8] text-[#1a1a1a]'
                : isDark ? 'text-[#52525b] hover:text-[#a1a1aa]' : 'text-[#999] hover:text-[#666]'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Two-panel layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Sessions List */}
        <div className={`${cardClass} lg:col-span-2 overflow-hidden`}>
          <div className={`max-h-[600px] overflow-y-auto divide-y ${isDark ? 'divide-[#27272a]' : 'divide-[#e5e5e5]'}`}>
            {loading ? (
              <div className="p-8 text-center animate-pulse">
                <span className={isDark ? 'text-[#52525b]' : 'text-[#999]'}>Loading...</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className={`p-8 text-center text-sm ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>No sessions found</div>
            ) : filtered.map(s => (
              <button
                key={s.id}
                onClick={() => setSelected(s)}
                className={`w-full px-4 py-3 text-left transition-colors ${
                  selected?.id === s.id
                    ? isDark ? 'bg-[#18181b]' : 'bg-[#f5f5f5]'
                    : isDark ? 'hover:bg-[#18181b]/50' : 'hover:bg-[#fafafa]'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{s.tutor_name} → {s.student_name}</span>
                  <div className="flex items-center gap-1">
                    {isMismatch(s) && <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-amber-400/10 text-amber-400">MISMATCH</span>}
                    {confIcon(s.speaker_id_confidence)}
                  </div>
                </div>
                <div className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                  LLM: {s.identified_tutor_speaker || '?'} → {s.identified_student_speaker || '?'}
                </div>
                <div className={`text-xs mt-0.5 ${isDark ? 'text-[#3f3f46]' : 'text-[#ccc]'}`}>
                  {s.lesson_timestamp ? new Date(s.lesson_timestamp).toLocaleDateString([], { day: 'numeric', month: 'short' }) : '--'}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Detail Panel */}
        <div className={`${cardClass} lg:col-span-3 p-5`}>
          {!selected ? (
            <div className={`text-center py-16 text-sm ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Select a session to view details</div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Speaker Identification</h3>
                <button
                  onClick={() => {
                    const blob = new Blob([JSON.stringify({ booking: { tutor: selected.tutor_name, student: selected.student_name }, identified: { tutor: selected.identified_tutor_speaker, student: selected.identified_student_speaker }, confidence: selected.speaker_id_confidence, raw: selected.speaker_id_raw_response }, null, 2)], { type: 'application/json' });
                    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `speaker-id-${selected.id}.json`; a.click();
                  }}
                  className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-[#18181b] text-[#52525b]' : 'hover:bg-[#f0f0f0] text-[#999]'}`}
                >
                  <Download className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* Comparison */}
              <div className="grid grid-cols-2 gap-3">
                <div className={`p-3 rounded-lg ${isDark ? 'bg-[#18181b]' : 'bg-[#f5f5f5]'}`}>
                  <span className={`text-[10px] uppercase tracking-wider ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Booking</span>
                  <div className={`text-sm font-medium mt-1 ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Tutor: {selected.tutor_name}</div>
                  <div className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Student: {selected.student_name}</div>
                </div>
                <div className={`p-3 rounded-lg border ${
                  isMismatch(selected)
                    ? isDark ? 'bg-amber-400/5 border-amber-400/20' : 'bg-amber-50 border-amber-200'
                    : isDark ? 'bg-emerald-400/5 border-emerald-400/20' : 'bg-emerald-50 border-emerald-200'
                }`}>
                  <span className={`text-[10px] uppercase tracking-wider ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>LLM Identified</span>
                  <div className={`text-sm font-medium mt-1 ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Tutor: {selected.identified_tutor_speaker || '—'}</div>
                  <div className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Student: {selected.identified_student_speaker || '—'}</div>
                </div>
              </div>

              {/* Confidence */}
              <div className="flex items-center gap-2">
                {confIcon(selected.speaker_id_confidence)}
                <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Confidence: {selected.speaker_id_confidence || 'Unknown'}</span>
              </div>

              {/* Raw Response */}
              {selected.speaker_id_raw_response && (
                <div>
                  <span className={`text-xs font-medium ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Raw LLM Response</span>
                  <pre className={`mt-1 p-3 rounded-lg text-xs overflow-x-auto max-h-48 overflow-y-auto ${isDark ? 'bg-[#18181b] text-[#a1a1aa]' : 'bg-[#f5f5f5] text-[#666]'}`}>
                    {typeof selected.speaker_id_raw_response === 'string' ? selected.speaker_id_raw_response : JSON.stringify(selected.speaker_id_raw_response, null, 2)}
                  </pre>
                </div>
              )}

              {/* Timestamps */}
              <div className={`pt-3 border-t ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
                <div className={`text-xs space-y-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                  <div>Lesson: {selected.lesson_timestamp ? new Date(selected.lesson_timestamp).toLocaleString() : '--'}</div>
                  <div>Identified: {selected.speakers_identified_at ? new Date(selected.speakers_identified_at).toLocaleString() : '--'}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeveloperSpeakerId;
